import React, { useState, useMemo, useEffect, useRef } from 'react';
import '../styles/Projects.css';

// 自動取得分類與描述
const CATEGORY_INFO = [
  { key: '北歐風格', title: '北歐風格', description: '結合自然元素與功能性設計，創造溫馨舒適的居家環境' },
  { key: '現代風格', title: '現代風格', description: '以簡潔的線條與中性色調打造寬敞明亮的空間' },
  { key: '新古典風', title: '新古典風', description: '融合現代與古典元素，展現優雅細緻的空間美學' },
  { key: '工業風格', title: '工業風格', description: '利用原始建材與粗獷質感，打造獨特且具個性的空間' },
  { key: '舊屋翻新', title: '舊屋翻新', description: '賦予老屋新生命，結合現代機能與美感' },
  { key: '商業空間', title: '商業空間', description: '專為商業需求設計的空間，兼顧品牌形象與實用功能' },
  { key: '民宿飯店', title: '民宿飯店', description: '打造獨特住宿體驗，結合當地特色與舒適氛圍' },
];

const allImages = require.context('../images/projects', true, /\.(jpe?g|png|webp)$/);
function getImagesByCategory(category) {
  return allImages
    .keys()
    .filter((key) => key.startsWith(`./${category}/`))
    .sort()
    .map(allImages);
}

function useResponsiveImagesPerPage() {
  const [imagesPerPage, setImagesPerPage] = useState(window.innerWidth <= 576 ? 1 : 6);
  useEffect(() => {
    const onResize = () => setImagesPerPage(window.innerWidth <= 576 ? 1 : 6);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return imagesPerPage;
}

const Projects = () => {
  const [currentCategory, setCurrentCategory] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [gridVisible, setGridVisible] = useState(true);
  const [enlargedImage, setEnlargedImage] = useState(null);
  const galleryRef = useRef(null);
  const imagesPerPage = useResponsiveImagesPerPage();

  const projects = useMemo(() =>
    CATEGORY_INFO.map(cat => ({
      ...cat,
      images: getImagesByCategory(cat.key),
    })),
    []
  );

  // ESC 關閉模態框
  useEffect(() => {
    if (!enlargedImage) return;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setEnlargedImage(null);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [enlargedImage]);

  // 切換分類時自動回到第一頁
  useEffect(() => {
    setCurrentPage(0);
    if (galleryRef.current && imagesPerPage === 1) {
      galleryRef.current.scrollTo({ left: 0, behavior: 'auto' });
    }
  }, [currentCategory, imagesPerPage]);

  const currentProject = projects[currentCategory];
  const totalPages = Math.ceil(currentProject.images.length / imagesPerPage);

  // 分頁切換（點擊圓點）
  const handlePageChange = (pageIdx) => {
    if (imagesPerPage === 1) {
      setCurrentPage(pageIdx);
      if (galleryRef.current) {
        const galleryWidth = galleryRef.current.offsetWidth;
        galleryRef.current.scrollTo({ left: pageIdx * galleryWidth, behavior: 'smooth' });
      }
    } else {
      setGridVisible(false);
      setTimeout(() => {
        setCurrentPage(pageIdx);
        setGridVisible(true);
      }, 180);
    }
  };

  // 滑動時自動偵測目前頁數（僅手機）
  const handleScroll = () => {
    if (galleryRef.current && imagesPerPage === 1) {
      const galleryWidth = galleryRef.current.offsetWidth;
      const page = Math.round(galleryRef.current.scrollLeft / galleryWidth);
      if (page !== currentPage) setCurrentPage(page);
    }
  };

  // 分頁資料
  const pages = [];
  for (let i = 0; i < totalPages; i++) {
    pages.push(currentProject.images.slice(i * imagesPerPage, (i + 1) * imagesPerPage));
  }

  // 點擊圖片放大
  const handleImageClick = (src) => {
    setEnlargedImage(src);
  };

  // 點擊模態框背景或關閉
  const handleCloseModal = (e) => {
    if (
      e.target.classList.contains('image-modal') ||
      e.target.classList.contains('close-modal') ||
      e.target.classList.contains('modal-img')
    ) {
      setEnlargedImage(null);
    }
  };

  return (
    <section className="projects" id="projects">
      <div className="projects-container">
        <div className="projects-header">
          <h2 className="section-title">精選案例</h2>
        </div>
        <div className="projects-tabs">
          {projects.map((cat, idx) => (
            <button
              key={cat.key}
              className={`projects-tab${idx === currentCategory ? ' active' : ''}`}
              onClick={() => setCurrentCategory(idx)}
            >
              {cat.title}
            </button>
          ))}
        </div>
        <div className="projects-showcase">
          <div className="project-item">
            <div className="project-info">
              <h3 className="project-title">{currentProject.title}</h3>
              <p className="project-description">{currentProject.description}</p>
            </div>
            {imagesPerPage === 1 ? (
              <div
                className="project-gallery gallery-scroll gallery-mobile"
                ref={galleryRef}
                onScroll={handleScroll}
              >
                {pages.length === 0 ? (
                  <div style={{textAlign: 'center', color: '#aaa', width: '100%'}}>暫無圖片</div>
                ) : (
                  pages.map((images, pageIdx) => (
                    <div
                      className="gallery-page"
                      key={pageIdx}
                      style={{ minWidth: '100%', maxWidth: '100%', display: 'flex', gap: 0, justifyContent: 'center' }}
                    >
                      {images.map((image, idx) => (
                        <div className="gallery-item" key={idx} style={{ width: '100%' }} onClick={() => handleImageClick(image)}>
                          <img 
                            src={image} 
                            alt={`${currentProject.title}室內設計案例作品 - 聯漢設計工作室第${pageIdx * imagesPerPage + idx + 1}張作品圖片`}
                            loading="lazy"
                          />
                        </div>
                      ))}
                    </div>
                  ))
                )}
              </div>
            ) : (
              <div className={`project-gallery project-gallery-grid fade-grid${gridVisible ? ' visible' : ''}`}>
                {pages.length === 0 ? (
                  <div style={{textAlign: 'center', color: '#aaa', width: '100%'}}>暫無圖片</div>
                ) : (
                  pages[currentPage].map((image, idx) => (
                    <div className="gallery-item" key={idx} onClick={() => handleImageClick(image)}>
                      <img 
                        src={image} 
                        alt={`${currentProject.title}室內設計案例作品 - 聯漢設計工作室第${currentPage * imagesPerPage + idx + 1}張作品圖片`}
                        loading="lazy"
                      />
                    </div>
                  ))
                )}
              </div>
            )}
            {/* 分頁導航 */}
            {totalPages > 1 && (
              <div className="slider-dots">
                {Array.from({ length: totalPages }).map((_, idx) => (
                  <span
                    key={idx}
                    className={`dot${idx === currentPage ? ' active' : ''}`}
                    onClick={() => handlePageChange(idx)}
                    aria-label={`切換到第${idx + 1}頁`}
                    tabIndex={0}
                    role="button"
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
      {/* 圖片放大模態框 */}
      {enlargedImage && (
        <div className="image-modal" onClick={handleCloseModal}>
          <div className="modal-content">
            <img src={enlargedImage} alt="放大的設計案例" className="modal-img" onClick={handleCloseModal} />
            <button className="close-modal" onClick={handleCloseModal}>×</button>
          </div>
        </div>
      )}
    </section>
  );
}

export default Projects; 